import {
  ActionIcon,
  Badge,
  Box,
  Group,
  type MantineSize,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import type { Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useState } from 'react';
import { isNonNullish } from 'remeda';

import { type LightAutomation, useUpdateAutomation } from '../../../../api';

import { RowDragHandleCell } from './DraggableRow';

export const MODEL_TRANSLATION_MAP: Record<string, string> = {
  invoice: 'INVOICE',
  debtor: 'CUSTOMER',
};

type TargetModelCellProps = {
  model: string;
  size?: MantineSize;
};

export const TargetModelCell = ({ model, size }: TargetModelCellProps) => {
  const { t } = useTranslation();

  const translationKey = MODEL_TRANSLATION_MAP[model];
  const displayName = t(tKeysLoose.AUTOMATION.MODELS[translationKey]);

  return (
    <Badge size={size} variant="light" color={model === 'invoice' ? 'purple' : 'coral'}>
      {displayName}
    </Badge>
  );
};

type ActionIconsCellProps = {
  automation: LightAutomation;
  handleDeleteAutomation: (id: string) => void;
  setEditAutomation: (id: string) => void;
};

export const ActionIconsCell = ({
  automation,
  handleDeleteAutomation,
  setEditAutomation,
}: ActionIconsCellProps) => {
  const { t } = useTranslation();
  const { updateAutomation } = useUpdateAutomation();

  const handleDisabledAutomation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;
    setIsActive(isChecked);
    updateAutomation({ id: automation.id, active: isChecked });
  };

  const [isActive, setIsActive] = useState(automation.active);

  return (
    <Group gap="space3">
      <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <Tooltip
          label={t(
            isActive ? i18nKeys.AUTOMATION.ACTIONS.DISABLE : i18nKeys.AUTOMATION.ACTIONS.ENABLE,
          )}
          refProp="rootRef"
        >
          <Switch
            color="purple.4"
            size="xs"
            variant="light"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleDisabledAutomation(e);
            }}
          />
        </Tooltip>
      </Box>
      <Tooltip label={t(tKeysLoose.AUTOMATION.ACTIONS.EDIT)}>
        <ActionIcon
          variant="light"
          color="purple"
          size="input-xs"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setEditAutomation(automation.id);
          }}
        >
          <IconEdit stroke={1.5} size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t(i18nKeys.AUTOMATION.ACTIONS.DELETE)}>
        <ActionIcon
          variant="light"
          color="red"
          size="input-xs"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleDeleteAutomation(automation.id);
          }}
        >
          <IconTrash stroke={1.5} size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

type LastRunAtCellProps = {
  lastRunAt: string | null;
};

export const LastRunAtCell = ({ lastRunAt }: LastRunAtCellProps) => {
  const { t } = useTranslation();

  if (isNonNullish(lastRunAt)) return <Text size="sm">{dayjs(lastRunAt).format('LLL')}</Text>;

  return (
    <Text size="sm" c="dimmed">
      {t(i18nKeys.AUTOMATION.STATUS.NEVER)}
    </Text>
  );
};

type DescriptionCellProps = {
  description: string | null;
};

export const DescriptionCell = ({ description }: DescriptionCellProps) => {
  const { t } = useTranslation();

  if (!description)
    return (
      <Text size="sm" fs="italic" c="dimmed">
        {t(i18nKeys.AUTOMATION.DRAG_AND_DROP_T.NO_DESCRIPTION)}
      </Text>
    );

  return (
    <Text size="sm" c="dimmed" lineClamp={2}>
      {description}
    </Text>
  );
};

type NameCellProps = {
  name: string;
};

export const NameCell = ({ name }: NameCellProps) => (
  <Text fw={500} style={{ whiteSpace: 'nowrap' }}>
    {name}
  </Text>
);

type DragHandleCellProps = {
  row: Row<LightAutomation>;
};

export const DragHandleCell = ({ row }: DragHandleCellProps) => (
  <RowDragHandleCell rowId={row.id} />
);
